const _excluded = ["data"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { orderBy } from 'lodash-es';
import { Page, TableList, Typography, PageEmptyState, PageErrorStateIcon, Button, PageContentHeader, Callout, IconButton } from 'seagull';
import { Plus, TrashLine } from 'seagull/icons';
import InviteOrEditMemberModal from "../../components/InviteOrEditMemberModal";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { routes } from "../../constants";
import { useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { useOnMount } from "../../hooks/useOnMount";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { Intent } from "../../infra/access-control";
import { handlePromise } from "../../infra/utils";
import { UserRole } from "../../models";
import { selectors } from "../../store/state-selectors";
import AccountantAccessApprovalModal from "./components/AccountantAccessApprovalModal";
import RemoveAccountantModal from "./components/RemoveAccountantModal";
import RemoveFromTeamModal from "./components/RemoveFromTeamModal";
import MemberRow from "./MemberRow";
import styles from "./TeamPage.module.scss";
import { testPrefix } from "./TeamPage.utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const title = 'Team';
const headerItems = [{
  id: 'memberName',
  value: 'Member name'
}, {
  id: 'role',
  value: 'Role'
}, {
  id: 'status',
  value: 'Status'
}];
const columnDefs = [{
  width: [350, 400]
},
// Member name
{
  width: 120
},
// Role
{
  width: 220
} // Status
];
const accountantsColumnDefs = columnDefs;
const accountantsHeaderItems = [{
  value: 'Company Name'
}, {
  value: 'Date Added'
}, {
  value: 'Status'
}];
const roleOrder = {
  Owner: 1,
  Admin: 2,
  Member: 3
};
const roleToText = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member'
};
export function TeamPageWithData(props) {
  const {
    members,
    accountants,
    loading,
    refresh
  } = props;
  const userRole = useStore(selectors.user.role);
  const userId = useStore(selectors.user.id);
  const businessId = useStore(selectors.user.businessId);
  const {
    api,
    logBI
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const location = useLocation();
  const history = useHistory();
  const membersList = useMemo(() => {
    return orderBy(members.map(member => ({
      user: member,
      role: roleToText[member.effectiveRole()],
      status: member.isInvited() ? 'Pending' : 'Active'
    })), [m => roleOrder[m.role], 'status'], ['asc', 'asc']);
  }, [members]);
  const {
    invitedAccountants,
    activeAccountants
  } = useMemo(() => {
    const normalized = accountants || [];
    return {
      invitedAccountants: normalized.filter(acc => acc.isInvited()),
      activeAccountants: normalized.filter(acc => acc.isActive())
    };
  }, [accountants]);
  useOnMount(() => {
    var _location$state;
    if ((_location$state = location.state) !== null && _location$state !== void 0 && _location$state.openAccountantAccessRequest && invitedAccountants.length > 0) {
      onOpenAccountantAccessRequest(invitedAccountants[0]);
      history.replace(routes.team);
    }
  });
  const onChangeRole = member => {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(InviteOrEditMemberModal, {
          member: member,
          allMembers: members,
          onClose: dismissModal,
          onSuccess: refresh
        });
      }
    });
  };
  const onResendInvitation = async member => {
    if (!businessId) return;
    const {
      success
    } = await handlePromise(api.member.resendInvitation(businessId, member.id()));
    if (!success) {
      return overlayContext.showErrorToast('Failed to resend invitation', 'resend-invitation-failure');
    }
    overlayContext.showSuccessToast("Invitation resent to ".concat(member.fullName()), 'resend-invitation-success');
  };
  const onRemoveFromTeam = member => {
    logBI(12202, {
      id: member.id(),
      email: member.email()
    });
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/_jsx(RemoveFromTeamModal, _objectSpread({
        member: member,
        allMembers: members,
        onRefresh: refresh
      }, p))
    });
  };
  const onInviteMember = () => {
    overlayContext.showModal({
      disableEnforceFocus: true,
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/_jsx(InviteOrEditMemberModal, {
          onClose: dismissModal,
          onSuccess: refresh,
          allMembers: members
        });
      }
    });
  };
  const onOpenAccountantAccessRequest = accountant => {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref3 => {
        let {
          dismissModal
        } = _ref3;
        return /*#__PURE__*/_jsx(AccountantAccessApprovalModal, {
          accountant: accountant,
          dismissModal: dismissModal,
          refresh: refresh
        });
      }
    });
  };
  const onRemoveAccountAccess = accountant => {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/_jsx(RemoveAccountantModal, _objectSpread(_objectSpread({}, p), {}, {
        accountant: accountant,
        refresh: refresh
      }))
    });
  };
  return /*#__PURE__*/_jsxs(Page, {
    header: "".concat(title, " (").concat(membersList.length, ")"),
    subheader: "Add and manage your team members",
    loading: loading,
    testId: testPrefix + '-screen',
    actions: /*#__PURE__*/_jsx(Button, {
      intent: Intent.InviteTeamMember,
      prefixIcon: /*#__PURE__*/_jsx(Plus, {}),
      onClick: onInviteMember,
      testId: testPrefix + '-btn-invite-member',
      children: "Invite team member"
    }),
    children: [invitedAccountants.length > 0 && /*#__PURE__*/_jsx(PageContentHeader, {
      children: /*#__PURE__*/_jsx("div", {
        className: styles.callouts,
        children: invitedAccountants.map(acc => /*#__PURE__*/_jsx(Callout, {
          withAction: /*#__PURE__*/_jsx(Button, {
            skin: "primaryOutline",
            intent: Intent.OpenAccountantAccessRequest,
            testId: testPrefix + '-view-acc-request',
            onClick: () => onOpenAccountantAccessRequest(acc),
            children: "View details"
          }),
          children: "You have a pending Accountant Access request"
        }, acc.id()))
      })
    }), /*#__PURE__*/_jsxs(TableList.Provider, {
      columnDefs: columnDefs,
      hasActionColumn: true,
      children: [/*#__PURE__*/_jsx(Typography, {
        weight: "normal",
        className: styles.tableTitle,
        children: "Team members"
      }), /*#__PURE__*/_jsx(TableList.Header, {
        items: headerItems,
        testId: "".concat(testPrefix, "-table-header")
      }), /*#__PURE__*/_jsx("div", {
        children: membersList.map((data, idx) => /*#__PURE__*/_jsx(MemberRow, {
          data: data,
          testId: "".concat(testPrefix, "-row-").concat(idx),
          adminOrMemberActingOnOwner: data.user.effectiveRole() === UserRole.Owner && (userRole == UserRole.Admin || userRole == UserRole.Member),
          isEditingTheSoleOwner: data.user.isOwner() && members.filter(m => m.isOwner()).length === 1,
          isCurrentUser: data.user.id() === userId,
          onChangeRole: onChangeRole,
          onResendInvitation: onResendInvitation,
          onRemoveFromTeam: onRemoveFromTeam
        }, data.user.id()))
      })]
    }), activeAccountants.length > 0 && /*#__PURE__*/_jsxs(TableList.Provider, {
      columnDefs: accountantsColumnDefs,
      hasActionColumn: true,
      children: [/*#__PURE__*/_jsx(Typography, {
        weight: "normal",
        className: cn(styles.tableTitle, styles.secondTableTitle),
        children: "Accountants with access to your business"
      }), /*#__PURE__*/_jsx(TableList.Header, {
        items: accountantsHeaderItems
      }), /*#__PURE__*/_jsx("div", {
        "data-testid": testPrefix + '-acc-table-body',
        children: activeAccountants.map((acc, idx) => /*#__PURE__*/_jsx(TableList.Row, {
          items: [{
            value: acc.companyName()
          }, {
            value: acc.formattedDate()
          }, {
            value: /*#__PURE__*/_jsx(Typography, {
              skin: "success",
              children: "Active"
            })
          }],
          actionColumn: /*#__PURE__*/_jsx(IconButton, {
            skin: "error",
            intent: Intent.RemoveAccountAccess,
            testId: "".concat(testPrefix, "-").concat(idx, "-rm-acc-access"),
            onClick: () => onRemoveAccountAccess(acc),
            children: /*#__PURE__*/_jsx(TrashLine, {})
          })
        }, acc.id()))
      })]
    })]
  });
}
export default function TeamPage() {
  const {
    api
  } = useStoreActions();
  useSetDocumentTitle(title);
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    fetch: () => api.member.getAll(),
    renderChild: _ref4 => {
      let {
          data
        } = _ref4,
        rest = _objectWithoutProperties(_ref4, _excluded);
      return /*#__PURE__*/_jsx(TeamPageWithData, _objectSpread(_objectSpread({}, data), rest));
    },
    renderError: _ref5 => {
      let {
        refresh
      } = _ref5;
      return /*#__PURE__*/_jsx(PageEmptyState, {
        testId: "".concat(testPrefix, "-error-page"),
        illustration: /*#__PURE__*/_jsx(PageErrorStateIcon, {}),
        message: "Something went wrong. Try to refresh the data",
        actions: /*#__PURE__*/_jsx(Button, {
          onClick: () => refresh(),
          testId: "".concat(testPrefix, "-refresh-btn"),
          children: "Refresh"
        })
      });
    }
  });
}